/**
 * ConfidenceCheck — slider 0–100 com pergunta única.
 *
 * Slide do tipo mission com missionId='lab.assess.confidence'. Captura
 * autopercepção de domínio antes/depois (pré/pós via pairId).
 *
 * Schema do frontmatter — ver SPEC-ATLAS-V2 §2.5.
 */

import { useState } from 'react'

import { useAdapter } from '../../adapter'
import type { MissionComponentProps } from '../../games/atlas/components'

interface Scale {
  min?: number
  max?: number
  step?: number
  labels?: Record<string, string>
}

export function ConfidenceCheck(props: MissionComponentProps) {
  const adapter = useAdapter()
  const config = props.slideConfig ?? {}
  const slideIndex = props.slideIndex ?? 0
  const prompt = (config.prompt as string) || ''
  const scale = (config.scale as Scale) || {}
  const min = scale.min ?? 0
  const max = scale.max ?? 100
  const step = scale.step ?? 1
  const labels = scale.labels ?? {}
  const pairId = (config.pairId as string) || ''
  const showPrev = Boolean(config.showPrevValue)
  const prev = pairId ? adapter.getPairedResponse(pairId) : null
  const prevValue =
    showPrev && prev && prev.kind === 'confidence' && prev.slideIndex !== slideIndex
      ? Number(prev.value)
      : null

  const initial =
    prev && prev.kind === 'confidence' && prev.slideIndex === slideIndex
      ? Number(prev.value)
      : Math.round((min + max) / 2)
  const [value, setValue] = useState(initial)
  const [submitted, setSubmitted] = useState(
    prev && prev.kind === 'confidence' && prev.slideIndex === slideIndex,
  )

  function handleSubmit() {
    if (!pairId) return
    adapter.submitConfidence({ pairId, slideIndex, value })
    setSubmitted(true)
    if (props.onMissionEvent) {
      props.onMissionEvent('submitted', { pairId, value })
    }
  }

  const labelAt = (n: number): string | undefined =>
    labels[String(n)] ?? undefined

  return (
    <div
      style={{
        maxWidth: 720,
        margin: '0 auto',
        padding: 'var(--spacing-lab-5, 24px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-lab-4, 16px)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--text-lab-xl, 1.5rem)' }}>{prompt}</h2>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={props.readOnly}
          onChange={(e) => setValue(Number(e.target.value))}
          style={{ flex: 1 }}
        />
        <output
          style={{
            minWidth: 64,
            textAlign: 'center',
            fontSize: 'var(--text-lab-2xl, 2rem)',
            fontWeight: 600,
            color: 'var(--color-lab-accent, #5B2DB8)',
          }}
        >
          {value}
        </output>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85em', opacity: 0.7 }}>
        <span>{labelAt(min) ?? min}</span>
        {labelAt(Math.round((min + max) / 2)) && (
          <span>{labelAt(Math.round((min + max) / 2))}</span>
        )}
        <span>{labelAt(max) ?? max}</span>
      </div>

      {prevValue !== null && (
        <aside
          style={{
            padding: 12,
            background: 'var(--color-lab-bg-2, #F2F2ED)',
            borderLeft: '3px solid var(--color-lab-accent, #6aa)',
            fontSize: '0.9em',
          }}
        >
          <div style={{ opacity: 0.7, marginBottom: 4 }}>Você respondeu antes:</div>
          <div style={{ fontWeight: 600 }}>{prevValue}</div>
        </aside>
      )}

      <button
        onClick={handleSubmit}
        disabled={props.readOnly || !pairId}
        style={{
          padding: '8px 24px',
          background: submitted
            ? 'var(--color-lab-accent, #6aa)'
            : 'var(--color-lab-accent, #5B2DB8)',
          color: 'white',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 'var(--text-lab-base, 1rem)',
          alignSelf: 'flex-end',
        }}
      >
        {submitted ? 'Atualizar' : 'Enviar'}
      </button>
    </div>
  )
}
